// Global "session expired" handling. Any axios call (saga or direct) that
// comes back UNAUTHORIZED sends the user to /login, and after a successful
// login they are sent back to the page they were on.
//
// Wiring:
//   - imported once in src/index.js (registers the axios interceptor)
//   - <AuthNavigatorBridge /> in App.js hands us react-router's navigate
//   - Login container reads getLoginRedirect() / clearLoginRedirect()
import axios from "axios";

const FROM_KEY = "loginRedirectFrom";

// Pages that handle UNAUTHORIZED themselves and must not be sent to /login.
// e.g. /digital?value=... shows its own login view (A_Redeem container).
const NO_REDIRECT_PATHS = ["/login", "/digital"];

let navigator = null;

export const setAuthNavigator = (navigate) => {
  navigator = navigate;
};

const isUnauthorized = (error) =>
  error?.response?.status === 401 ||
  error?.response?.data?.status?.code === "UNAUTHORIZED";

// Also stored in sessionStorage because many screens still call
// navigate("/login") without state from their own error handlers.
export const getLoginRedirect = () => {
  try {
    return sessionStorage.getItem(FROM_KEY);
  } catch {
    return null;
  }
};

export const clearLoginRedirect = () => {
  try {
    sessionStorage.removeItem(FROM_KEY);
  } catch {}
};

const redirectToLogin = () => {
  const { pathname, search } = window.location;
  // Already on login (e.g. a failed OTP call) or on an excluded page.
  if (NO_REDIRECT_PATHS.includes(pathname.toLowerCase())) return;

  const from = pathname + search;
  // Drop the rejected token, otherwise Login bounces a "logged in" user to "/".
  localStorage.removeItem("UserJWTAuth");
  try {
    sessionStorage.setItem(FROM_KEY, from);
  } catch {}

  if (navigator) navigator("/login", { state: { from } });
  else window.location.assign("/login");
};

axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isUnauthorized(error)) redirectToLogin();
    // Still reject so each caller's own handleError runs (loaders etc.).
    return Promise.reject(error);
  }
);
